// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Local alerts: an optional in-page beep (Web Audio) and an optional system
 * notification. Both fire only on the user's own device; the notification's text
 * carries no data and is never sent anywhere.
 */

export function notificationsSupported(): boolean {
  return 'Notification' in window;
}

export function notificationPermission(): NotificationPermission {
  return notificationsSupported() ? Notification.permission : 'denied';
}

/** Request permission — must be called from a user gesture on some browsers. */
export async function requestNotifications(): Promise<boolean> {
  if (!notificationsSupported()) return false;
  try {
    const result = await Notification.requestPermission();
    return result === 'granted';
  } catch {
    return false;
  }
}

export function notifyMotion(body: string): void {
  if (!notificationsSupported() || Notification.permission !== 'granted') return;
  try {
    new Notification('Motion detected — Sentrywell', {
      body,
      tag: 'sentrywell-motion',
      silent: false,
    });
  } catch {
    /* some browsers require a service-worker registration for notifications */
  }
}

// ── Beep via Web Audio ────────────────────────────────────────────────────────

let ctx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  const Ctor =
    (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  return ctx;
}

/** Short two-tone chirp. Safe to call repeatedly. */
export function beep(): void {
  const ac = audioContext();
  if (!ac) return;
  if (ac.state === 'suspended') void ac.resume();
  const now = ac.currentTime;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.22, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.32);
  gain.connect(ac.destination);
  const osc = ac.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(880, now);
  osc.frequency.setValueAtTime(1320, now + 0.16);
  osc.connect(gain);
  osc.start(now);
  osc.stop(now + 0.34);
}
